import { useRef, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { SingleValue } from 'react-select';

import useGetContractDetails from '@/hooks/contract/useGetContractDetails.hook';
import { EDITABLE_STATUS_KEYS } from '@/config/statuses.config';
import useGetContractLinesFromCache from '@/hooks/contract/useGetContractLinesFromCache.hook';
import IntervalSelector from './components/IntervalSelector';
import AgConfirmationModal from '@/components/common/ModalWindow/AgConfirmationModal';
import useUpdateBillingInterval from './hooks/useUpdateBillingInterval';
import ActionsCard from '../../../../../components/common/ActionsCard/ActionsCard';
import {
  useSelectedLines
} from '@/pages/ContractDetails/ContractLines/components/ContractLinesGrid/linesSelectionObservable';
import { useSelectedVariableLines }
  from '@/pages/ContractDetails/ContractLines/components/ContractLinesGrid/variableLinesSelectionObservable';
import { ContractStatusEnum, ContractBillingStatusPascalCaseEnum } from '@/graphql/contracts/_generated_/contracts';

export type IntervalData = {
  billingInterval: string;
  formattedBillingInterval: string;
  __typename: string;
  monthCount?: number;
};

export type UpdatedCL = {
  contractId: string;
  contractLines: { contractLineID: string }[];
  billingInterval: string;
};

const defaultCLData = {
  contractId: '',
  contractLines: [],
  billingInterval: '',
};

export default function BillingIntervalContainer() {
  const { contractId }: { contractId: UUID } = useParams();
  const { contract } = useGetContractDetails(contractId);
  const [updatedContractLines, setUpdatedContractLines] = useState<UpdatedCL>(defaultCLData);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const selectedLineRows = useSelectedLines();
  const selectedVariableLineRows = useSelectedVariableLines();
  const variableSelected = Boolean(selectedVariableLineRows?.length);
  const { updateBillingInterval } = useUpdateBillingInterval(contractId, variableSelected);
  const { contractHasActiveSubscriptionLines, cachedBCContractLines, cachedVariableContractLines } =
    useGetContractLinesFromCache(contractId);

  const selectIntervalRef = useRef<any>(null);

  const hasSelectedLinesPaused = useMemo(() => {
    const allSelectedLines = [...selectedLineRows, ...selectedVariableLineRows];
    if (allSelectedLines.length === 0) return false;

    const bcLines = cachedBCContractLines?.paginatedFilteredContractLinesByBillingCycles?.items || [];
    const variableLines = cachedVariableContractLines?.paginatedFilteredVariableContractLines?.items || [];

    const selectedLineIds = allSelectedLines.map(line => line.id);

    const selectedBcLines = bcLines.filter(line => line?.id && selectedLineIds.includes(line.id));
    const selectedVariableLines = variableLines.filter(line => line?.iD && selectedLineIds.includes(line.iD));

    const bcLinesPaused = selectedBcLines.some(line =>
      line?.lineBillingStatus === ContractBillingStatusPascalCaseEnum.Paused ||
      line?.lineBillingStatus === ContractBillingStatusPascalCaseEnum.FuturePause
    );

    const variableLinesPaused = selectedVariableLines.some(line =>
      line?.lineBillingStatus === ContractBillingStatusPascalCaseEnum.Paused ||
      line?.lineBillingStatus === ContractBillingStatusPascalCaseEnum.FuturePause
    );

    return bcLinesPaused || variableLinesPaused;
  }, [selectedLineRows, selectedVariableLineRows, cachedBCContractLines, cachedVariableContractLines]);

  const hasPauseActive = hasSelectedLinesPaused;

  const linesFromBothGridsSelected = Boolean(selectedLineRows.length && selectedVariableLineRows.length);
  const linesFromOneGridSelected = Boolean(selectedLineRows.length || selectedVariableLineRows.length);
  const selectedLines = selectedLineRows.length ? selectedLineRows : selectedVariableLineRows;

  const isVisible = contract &&
    (contractHasActiveSubscriptionLines || Boolean(selectedVariableLineRows.length)) &&
    !(selectedLines.some(x => !EDITABLE_STATUS_KEYS.includes(x.payload.contractLine.status as ContractStatusEnum))) &&
    !linesFromBothGridsSelected && linesFromOneGridSelected;

  const handleChange = (value: SingleValue<IntervalData>) => {
    if (value) {
      setUpdatedContractLines({
        contractId: contractId,
        contractLines: selectedLines.map(({ id }) => ({ contractLineID: id })),
        billingInterval: value.billingInterval,
      });
      setShowConfirmationModal(true);
    }
  };

  const onSubmit = () => {
    if (updatedContractLines.contractLines.length) {
      updateBillingInterval(updatedContractLines);
    }

    setShowConfirmationModal(false);
    selectIntervalRef.current.setValue('');
    setUpdatedContractLines(defaultCLData);
  };

  const onCancel = () => {
    setShowConfirmationModal(false);
    selectIntervalRef.current.setValue('');
  };

  if (!isVisible) {
    return null;
  }

  return (
    <ActionsCard>
      <>
        <AgConfirmationModal
          isOpen={showConfirmationModal}
          onConfirm={onSubmit}
          onClose={onCancel}
          title="Please confirm you wish to continue"
        >
          You are about to modify the Billing Interval for the Contract Line(s).<br />
          {selectedLineRows?.length ? 'This change could potentially impact Sales Order generation.' :
            'This change could impact Variable Contract Line\'s predicted dates calculation.'}<br />
          {hasPauseActive && (
            <>
              <br />
              <strong>Warning:</strong> This action will change the billing date.
              A Pause to billing is set. Please review your Pause End Date to ensure accurate billing.<br />
            </>
          )}
          Please confirm you wish to continue.
        </AgConfirmationModal>
        <IntervalSelector handleChange={handleChange} ref={selectIntervalRef} />
      </>
    </ActionsCard>
  );
}
